// src/Dashboard/pages/user/Galleries/AlbumGalerieDashboard.jsx

import React, { useMemo } from "react";
import createDOMPurify from "dompurify";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";

import { useAlbumGalerie } from "./hooks/useAlbumGalerie";

import AlbumHeader from "./components/AlbumHeader";
import AlbumKpis from "./components/AlbumKpis";
import PhotoCard from "./components/PhotoCard";
import PhotoSkeleton from "./components/PhotoSkeleton";
import PhotoDrawer from "./components/PhotoDrawer";
import RemovePhotoDrawer from "./components/RemovePhotoDrawer";
import PhotoLightbox from "./components/PhotoLightbox";
import GalleriesEmptyState from "./components/GalleriesEmptyState";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------
   DOMPurify et styles enrichis sécurisés
-------------------------------------------------------- */

const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : null;

const isColor = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const isSafeIndentSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (text === "0") return true;

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const maximumByUnit = {
    px: 160,
    rem: 10,
    em: 10,
  };

  return amount >= 0 && amount <= maximumByUnit[unit];
};

const isSafeFontSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  const match = text.match(/^(\d+(?:\.\d+)?)(px|pt|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 64 },
    pt: { min: 6, max: 48 },
    rem: { min: 0.5, max: 4 },
    em: { min: 0.5, max: 4 },
    "%": { min: 50, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const isSafeFontFamily = (value) => {
  const text = String(value || "").trim();

  if (!text || text.length > 120) return false;

  if (
    text.includes(";") ||
    text.includes("{") ||
    text.includes("}") ||
    text.includes("\\") ||
    /url\s*\(/i.test(text) ||
    /expression\s*\(/i.test(text) ||
    /var\s*\(/i.test(text)
  ) {
    return false;
  }

  return /^[-"'a-z0-9À-ÿ_,\s]+$/i.test(text);
};

const isSafeLineHeight = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const amount = Number(text);

    return amount >= 0.8 && amount <= 3;
  }

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 80 },
    rem: { min: 0.8, max: 5 },
    em: { min: 0.8, max: 5 },
    "%": { min: 80, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED_STYLES = {
    color: (value) => isColor(value),

    "background-color": (value) => isColor(value),

    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase(),
      ),

    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-size": (value) => isSafeFontSize(value),

    "font-family": (value) => isSafeFontFamily(value),

    "line-height": (value) => isSafeLineHeight(value),

    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "margin-left": (value) => isSafeIndentSize(value),

    "padding-left": (value) => isSafeIndentSize(value),

    "text-indent": (value) => isSafeIndentSize(value),

    "list-style-type": (value) =>
      [
        "none",
        "disc",
        "circle",
        "square",
        "decimal",
        "lower-alpha",
        "upper-alpha",
        "lower-roman",
        "upper-roman",
      ].includes(String(value).trim().toLowerCase()),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");

      if (separatorIndex === -1) return null;

      const property = rule
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();

      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED_STYLES[property]?.(value)
        ? `${property}: ${value}`
        : null;
    })
    .filter(Boolean)
    .join("; ");
};

if (purify) {
  purify.removeAllHooks();

  purify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const cleanStyle = sanitizeInlineStyle(data.attrValue);

      if (cleanStyle) {
        data.attrValue = cleanStyle;
      } else {
        data.keepAttr = false;
      }
    }
  });

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;

    const href = node.getAttribute("href") || "";

    if (/^\s*javascript:/i.test(href)) {
      node.removeAttribute("href");
    }

    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

/* -------------------------------------------------------
   Classes du contenu enrichi
-------------------------------------------------------- */

const RICH_TEXT_CLASSES = cn(
  "dashboard-album-rich-content",
  "prose prose-slate max-w-none break-words",
  "text-sm leading-7 text-slate-700 sm:text-base",
  "dark:prose-invert dark:text-slate-200",

  // Paragraphes
  "prose-p:my-3",
  "[&_p]:whitespace-pre-wrap",

  // Titres
  "prose-headings:my-3",
  "prose-headings:font-barlow",
  "prose-headings:font-bold",
  "prose-headings:text-slate-950",
  "dark:prose-headings:!text-white",

  // Texte enrichi
  "prose-strong:text-slate-950",
  "prose-b:text-slate-950",
  "dark:prose-strong:!text-white",
  "dark:prose-b:!text-white",
  "prose-a:font-bold",
  "prose-a:text-[#013E8E]",
  "hover:prose-a:underline",
  "dark:prose-a:text-indigo-300",

  // Listes à puces
  "[&_ul]:!my-4",
  "[&_ul]:!list-disc",
  "[&_ul]:!pl-6",

  // Listes numérotées
  "[&_ol]:!my-4",
  "[&_ol]:!list-decimal",
  "[&_ol]:!pl-6",

  // Éléments des listes
  "[&_li]:!my-1",
  "[&_li]:!pl-1",
  "[&_li]:whitespace-pre-wrap",

  // Sous-listes
  "[&_ul_ul]:!my-2",
  "[&_ul_ul]:!list-[circle]",
  "[&_ul_ul]:!pl-5",
  "[&_ul_ul_ul]:!list-[square]",

  "[&_ol_ol]:!my-2",
  "[&_ol_ol]:!list-[lower-alpha]",
  "[&_ol_ol]:!pl-5",
  "[&_ol_ol_ol]:!list-[lower-roman]",

  // Alignements Quill
  "[&_.ql-align-center]:!text-center",
  "[&_.ql-align-right]:!text-right",
  "[&_.ql-align-justify]:!text-justify",

  // Indentations Quill
  "[&_.ql-indent-1]:!ml-6",
  "[&_.ql-indent-2]:!ml-12",
  "[&_.ql-indent-3]:!ml-16",
  "[&_.ql-indent-4]:!ml-20",
  "[&_.ql-indent-5]:!ml-24",

  // Tailles Quill
  "[&_.ql-size-small]:!text-sm",
  "[&_.ql-size-large]:!text-xl",
  "[&_.ql-size-huge]:!text-3xl",

  // Polices Quill
  "[&_.ql-font-serif]:!font-serif",
  "[&_.ql-font-monospace]:!font-mono",

  // Citations
  "prose-blockquote:my-4",
  "prose-blockquote:border-[#013E8E]",
  "prose-blockquote:bg-blue-50",
  "prose-blockquote:px-4",
  "prose-blockquote:py-2",
  "prose-blockquote:not-italic",
  "dark:prose-blockquote:bg-blue-500/10",

  // Images intégrées
  "prose-img:mx-auto",
  "prose-img:rounded-xl",
  "prose-img:shadow-md",

  // Séparateurs et code
  "prose-hr:border-slate-200",
  "dark:prose-hr:border-white/10",
  "prose-code:break-words",

  // Marges externes
  "[&>*:first-child]:mt-0",
  "[&>*:last-child]:mb-0",
);

/* -------------------------------------------------------
   Helpers
-------------------------------------------------------- */

function stripHtml(html = "") {
  if (typeof document === "undefined") {
    return String(html || "")
      .replace(/<[^>]*>/g, "")
      .trim();
  }

  const element = document.createElement("div");

  element.innerHTML = html;

  return (
    element.textContent ||
    element.innerText ||
    ""
  ).trim();
}

function sanitizeHtml(html = "") {
  if (!purify) return "";

  return purify.sanitize(String(html || ""), {
    USE_PROFILES: {
      html: true,
    },
    ADD_ATTR: [
      "style",
      "target",
      "rel",
      "class",
      "data-list",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ["style"],
  });
}

/* -------------------------------------------------------
   Composant
-------------------------------------------------------- */

export default function AlbumGalerieDashboard() {
  useScrollToTop();

  const {
    navigate,
    gallery,
    loading,
    photos,
    visiblePhotos,
    stats,
    isManager,

    loadMore,

    photoDrawerOpen,
    photoDrawerMode,
    selectedPhoto,
    openCreatePhotoDrawer,
    openEditPhotoDrawer,
    closePhotoDrawer,
    handlePhotoSaved,

    removeDrawerOpen,
    openRemoveDrawer,
    closeRemoveDrawer,
    handlePhotoDeleted,

    lightboxOpen,
    lightboxIndex,
    openLightbox,
    closeLightbox,

    draggingIndex,
    overIndex,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,

    handleToggleCover,
    copyShare,
    refresh,
  } = useAlbumGalerie();

  const albumDescription = useMemo(() => {
    const rawDescription = gallery?.description || "";

    if (!stripHtml(rawDescription)) {
      return "";
    }

    return sanitizeHtml(rawDescription);
  }, [gallery?.description]);

  if (!loading && !gallery) {
    return (
      <section className="w-full pb-10 pt-5 font-satoshi">
        <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
          <GalleriesEmptyState
            message="Album introuvable"
            description="L’album demandé n’existe pas ou n’est plus disponible."
            onReset={() => navigate(-1)}
            resetLabel="Retour"
          />
        </div>
      </section>
    );
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <AlbumHeader
          gallery={gallery}
          loading={loading}
          photosCount={photos.length}
          isManager={isManager}
          onBack={() => navigate(-1)}
          onRefresh={refresh}
          onShare={copyShare}
          onAdd={openCreatePhotoDrawer}
          onRemove={openRemoveDrawer}
        />

        <AlbumKpis
          stats={stats}
          loading={loading}
          isManager={isManager}
        />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            {/* Description HTML enrichie */}

            {albumDescription ? (
              <div className="mb-5 rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
                <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Description de l’album
                </p>

                <div
                  className={RICH_TEXT_CLASSES}
                  style={{ tabSize: 4 }}
                  dangerouslySetInnerHTML={{
                    __html: albumDescription,
                  }}
                />
              </div>
            ) : null}

            {/* Liste des photos */}

            {loading ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 9 }).map(
                  (_, index) => (
                    <PhotoSkeleton key={index} />
                  ),
                )}
              </div>
            ) : photos.length === 0 ? (
              <GalleriesEmptyState
                message="Aucune photo pour l’instant"
                description="Ajoutez une première photo pour alimenter cet album."
                isManager={isManager}
                onCreate={openCreatePhotoDrawer}
                createLabel="Ajouter une photo"
              />
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {visiblePhotos.map(
                    (photo, index) => (
                      <PhotoCard
                        key={photo.id || index}
                        photo={photo}
                        index={index}
                        isManager={isManager}
                        dragging={
                          draggingIndex === index
                        }
                        over={overIndex === index}
                        onOpen={() =>
                          openLightbox(index)
                        }
                        onEdit={() =>
                          openEditPhotoDrawer(photo)
                        }
                        onToggleCover={(event) =>
                          handleToggleCover(
                            event,
                            photo,
                          )
                        }
                        onDragStart={(event) =>
                          onDragStart(event, index)
                        }
                        onDragOver={(event) =>
                          onDragOver(event, index)
                        }
                        onDragLeave={onDragLeave}
                        onDrop={(event) =>
                          onDrop(event, index)
                        }
                      />
                    ),
                  )}

                  {isManager ? (
                    <>
                      <button
                        type="button"
                        onClick={
                          openCreatePhotoDrawer
                        }
                        className="grid min-h-[210px] place-items-center rounded-[24px] border border-dashed border-slate-300 bg-slate-50 text-slate-600 transition hover:border-[#013E8E] hover:bg-blue-50 hover:text-[#013E8E] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-indigo-400 dark:hover:bg-indigo-500/10"
                      >
                        <span className="text-sm font-bold">
                          + Ajouter une photo
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={openRemoveDrawer}
                        className="grid min-h-[210px] place-items-center rounded-[24px] border border-dashed border-red-300 bg-red-50 text-red-700 transition hover:bg-red-100 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20"
                      >
                        <span className="text-sm font-bold">
                          Retirer une photo
                        </span>
                      </button>
                    </>
                  ) : null}
                </div>

                {visiblePhotos.length <
                photos.length ? (
                  <div className="mt-6 flex justify-center">
                    <button
                      type="button"
                      onClick={loadMore}
                      className="rounded-2xl bg-[#013E8E] px-5 py-3 text-sm font-bold text-white transition hover:bg-indigo-800"
                    >
                      Charger plus
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </section>
      </div>

      {lightboxOpen ? (
        <PhotoLightbox
          photos={photos}
          startIndex={lightboxIndex}
          onClose={closeLightbox}
        />
      ) : null}

      <PhotoDrawer
        isOpen={photoDrawerOpen}
        mode={photoDrawerMode}
        galleryId={gallery?.id}
        photo={selectedPhoto}
        onClose={closePhotoDrawer}
        onSaved={handlePhotoSaved}
      />

      <RemovePhotoDrawer
        isOpen={removeDrawerOpen}
        galleryId={gallery?.id}
        onClose={closeRemoveDrawer}
        onDeleted={handlePhotoDeleted}
      />

      <ToastContainer />
    </section>
  );
}